import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Search, User, Users, Home, Building2 } from 'lucide-react';
import { STATUS_COLORS } from './calendarUtils';
import { SHIFT_PERIODS, ShiftPeriodBadge } from './ShiftPeriodBadge';

const GROUP_OPTIONS = [
  { value: 'participant', label: 'By Participant', icon: Users },
  { value: 'staff', label: 'By Staff', icon: User },
  { value: 'sil', label: 'By SIL House', icon: Home },
  { value: 'centre', label: 'By Centre', icon: Building2 },
];

// Left filters panel for Calendar V3: search, status, period, group-by, legend.
export function CalendarFilters({ searchTerm, setSearchTerm, statusFilter, setStatusFilter, periodFilter, setPeriodFilter, groupBy, setGroupBy }) {
  const togglePeriod = (p) => {
    const set = new Set(periodFilter || []);
    if (set.has(p)) set.delete(p); else set.add(p);
    setPeriodFilter(Array.from(set));
  };
  return (
    <div className="w-52 flex-shrink-0 border-r bg-slate-50/50 p-3 space-y-4 overflow-y-auto" data-testid="filters-panel">
      <div>
        <Label className="text-xs font-medium text-muted-foreground mb-1 block">Search</Label>
        <div className="relative">
          <Search className="absolute left-2 top-2 w-3.5 h-3.5 text-muted-foreground" />
          <Input placeholder="Staff, participant..." className="pl-7 h-8 text-xs" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-testid="search-filter" />
        </div>
      </div>
      <div>
        <Label className="text-xs font-medium text-muted-foreground mb-1 block">Status</Label>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {Object.keys(STATUS_COLORS).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs font-medium text-muted-foreground mb-1 block">Shift Period</Label>
        <div className="flex flex-wrap gap-1" data-testid="period-filter">
          {SHIFT_PERIODS.map(p => {
            const active = (periodFilter || []).includes(p);
            return (
              <button key={p} onClick={() => togglePeriod(p)}
                className={`focus:outline-none ${active ? 'opacity-100 ring-2 ring-offset-1 ring-slate-400 rounded-full' : 'opacity-60 hover:opacity-100'}`}
                data-testid={`period-filter-${p}`}>
                <ShiftPeriodBadge period={p} showIcon={false} />
              </button>
            );
          })}
        </div>
        {(periodFilter?.length || 0) > 0 && (
          <button onClick={() => setPeriodFilter([])} className="text-[10px] text-muted-foreground hover:text-foreground mt-1" data-testid="period-filter-clear">Clear</button>
        )}
      </div>
      <Separator />
      <div>
        <Label className="text-xs font-medium text-muted-foreground mb-1 block">Group By</Label>
        <div className="space-y-1">
          {GROUP_OPTIONS.map(opt => (
            <button key={opt.value} onClick={() => setGroupBy(opt.value)}
              className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs transition-colors ${groupBy === opt.value ? 'bg-slate-900 text-white' : 'hover:bg-slate-100 text-slate-700'}`}>
              <opt.icon className="w-3.5 h-3.5" /> {opt.label}
            </button>
          ))}
        </div>
      </div>
      <Separator />
      <div>
        <Label className="text-xs font-medium text-muted-foreground mb-2 block">Legend</Label>
        <div className="space-y-1">
          {Object.entries(STATUS_COLORS).map(([s, c]) => (
            <div key={s} className="flex items-center gap-2 text-[10px]"><span className={`w-2.5 h-2.5 rounded-full ${c.dot}`} /><span className="text-slate-600">{s}</span></div>
          ))}
        </div>
      </div>
    </div>
  );
}
